/**
 * **THE TURN'S BEAT — every moment a fighter moves on, in one place.**
 *
 * A turn plays as one beat after the reveal: the fighter acts out its move, the blow lands at
 * CONTACT, and the fighter reacts. The drawn poses (`battlePoses.ts`), the mood flash
 * (`battleMood.ts`), the CSS motion (`BattleSprite.css`, `BattleSpritePixel.css`,
 * `BattleSpriteCombo.css`), the screen shake, the pixel effects and the combo's sound all hang off
 * these numbers. The CSS restates them as custom properties on the battle grid, and
 * `comboStrike.test.ts` fails if the two ever disagree.
 *
 * This module imports nothing from the battle UI so every one of those can import it.
 */

import { EXCHANGE_OVERWHELM_CARDS } from "@shared/domain/cardRules";

/** How long a transient turn-mood flash (hurt / won-turn) stays on the portrait. */
export const TURN_MOOD_FLASH_MS = 900;

/**
 * **How a turn's beat is split.** The fighter acts out its move, then reacts to how the turn went.
 * The two together fill exactly the mood flash ({@link TURN_MOOD_FLASH_MS}), and the CSS
 * (`--bs-move-ms`, `--bs-reaction-ms`) uses the same two numbers.
 */
export const SPRITE_MOVE_MS = 520;
export const SPRITE_REACTION_MS = TURN_MOOD_FLASH_MS - SPRITE_MOVE_MS;
/** When the attacker connects — 45% into the move, the same moment both CSS layers strike at. */
export const SPRITE_CONTACT_MS = Math.round(SPRITE_MOVE_MS * 0.45);
/**
 * One held frame of pixel motion (~16fps). The screen shake starts one frame after contact, on
 * the second white frame, so the target is seen white BEFORE the world moves.
 */
export const SPRITE_FRAME_MS = 60;

/* ── The combo: one blow per card ──────────────────────────────────────────────────────────── */

/**
 * **A STACK OF THREE OR FOUR CARDS THAT LANDS IS PLAYED AS A COMBO — one blow per card.**
 *
 * - **×3** is three quick blows and a counter to 3 HITS. No more: it is a strong round, not the
 *   rarest one.
 * - **×4**, the full commit ({@link EXCHANGE_OVERWHELM_CARDS}), is the moment the fight stops for:
 *   the fighter GATHERS ENERGY first, rushes in for three blows, leaps, and dives into the
 *   finisher.
 *
 * The number of blows IS the number of cards, so the counter counts what the move button's ×3 /
 * ×4 chip counted. It is a picture of ONE damage number — the engine still lands the move once.
 */
export const COMBO_MIN_CARDS = 3;
export const COMBO_MAX_CARDS = EXCHANGE_OVERWHELM_CARDS;
/** Between two of the quick blows: two frames of rebound and one of the next strike. */
export const COMBO_JAB_GAP_MS = 110;

/** ×4: the fighter gathers energy before it moves — aura, sparks rushing in, the arena dims. */
export const COMBO_CHARGE_MS = 480;
/** ×4: from the end of the charge to the first blow — the charge is the wind-up, so it is quick. */
export const COMBO_RUSH_MS = 110;
/** ×4: from the third blow to the finisher — the leap up, the arena dark, speed lines. */
export const COMBO_LEAP_MS = 260;
/** ×4: the finisher's hit-stop. The first two frames of it are the IMPACT FRAME. */
export const COMBO_HITSTOP_MS = 200;
/** ×4: the impact frame — the arena black, both fighters white, for two held frames. */
export const COMBO_IMPACT_FRAME_MS = 2 * SPRITE_FRAME_MS;
/** ×4: the launched target is in the air this long before it crashes down. */
export const COMBO_AIRTIME_MS = 200;

/** One combo's moments, in ms from the start of the beat. */
export interface ComboTimeline {
  /** Blows — one per card. */
  readonly hits: number;
  /** The energy gathered before the rush (0: none). */
  readonly chargeMs: number;
  /** When each blow lands. The last is the finisher on a ×4. */
  readonly hitMs: readonly number[];
  /** The finisher, or `null` when the combo has none (×3). */
  readonly finisherMs: number | null;
  /** When the target, frozen by the last blow, is let go (and a knocked-out one falls). */
  readonly releaseMs: number;
  /** When the launched target crashes down (×4), or `null`. */
  readonly landMs: number | null;
  /** The attacker's whole move. */
  readonly moveMs: number;
  /** The whole beat: the move, then the ordinary reaction. */
  readonly beatMs: number;
}

const jabs = (first: number, count: number) => Array.from({ length: count }, (_, i) => first + i * COMBO_JAB_GAP_MS);

/** ×3: three quick blows from the ordinary contact, and home. */
const TRIPLE_HITS = jabs(SPRITE_CONTACT_MS, 3);
const TRIPLE_MOVE_MS = Math.round(SPRITE_MOVE_MS * 1.5);
export const COMBO_TRIPLE: ComboTimeline = {
  hits: 3,
  chargeMs: 0,
  hitMs: TRIPLE_HITS,
  finisherMs: null,
  releaseMs: TRIPLE_HITS[2] + 2 * SPRITE_FRAME_MS,
  landMs: null,
  moveMs: TRIPLE_MOVE_MS,
  beatMs: TRIPLE_MOVE_MS + SPRITE_REACTION_MS,
};

/** ×4: charge, three blows, the leap, the finisher, the crash. */
const FULL_JABS = jabs(COMBO_CHARGE_MS + COMBO_RUSH_MS, 3);
const FULL_FINISHER_MS = FULL_JABS[2] + COMBO_LEAP_MS;
const FULL_MOVE_MS = SPRITE_MOVE_MS * 3;
export const COMBO_FULL: ComboTimeline = {
  hits: 4,
  chargeMs: COMBO_CHARGE_MS,
  hitMs: [...FULL_JABS, FULL_FINISHER_MS],
  finisherMs: FULL_FINISHER_MS,
  releaseMs: FULL_FINISHER_MS + COMBO_HITSTOP_MS,
  landMs: FULL_FINISHER_MS + COMBO_HITSTOP_MS + COMBO_AIRTIME_MS,
  moveMs: FULL_MOVE_MS,
  beatMs: FULL_MOVE_MS + SPRITE_REACTION_MS,
};

/** The timeline for a stack of `cards`, or `null` when it is too small to be a combo. */
export function comboTimeline(cards: number): ComboTimeline | null {
  if (cards >= COMBO_MAX_CARDS) return COMBO_FULL;
  if (cards >= COMBO_MIN_CARDS) return COMBO_TRIPLE;
  return null;
}

/**
 * Which side of a combo a fighter is on this turn — it `landed` the blows, or it `took` them —
 * and how many blows the combo has. `undefined` on every other turn.
 */
export interface ComboStrike {
  readonly role: "landed" | "took";
  readonly hits: number;
}

/** How long this turn's beat runs, so everything that waits for it waits the right time. */
export function turnBeatMs(combo: ComboStrike | undefined): number {
  return combo ? (comboTimeline(combo.hits)?.beatMs ?? TURN_MOOD_FLASH_MS) : TURN_MOOD_FLASH_MS;
}
